import { useState } from 'react';

import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useCoupons from 'api/useCoupons';
import usePermissions from 'api/usePermissions';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import dayjs from 'dayjs';
import { useUserPermissions } from 'hooks/useUserPermissions';

import EmptyState from 'components/EmptyState/EmptyState';

import { CouponsFilterBar } from './Components/CouponsFilterBar';
import CouponsStatusToggle from './Components/CouponsStatusToggle';
import CouponsToggleDialog from './Components/CouponsToggleDialog';
import Header from './Components/Header';
import { useCouponsFilters } from './hooks';

const CouponsLayout = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { canUserEdit } = usePermissions();
  const { updateCouponsConfiguration, getCouponsConfiguration, getCoupons } =
    useCoupons(publisherId);

  const [isCouponsDialogOpen, setIsCouponsDialogOpen] = useState(false);
  const isCouponsEnabled = Boolean(getCouponsConfiguration.data?.enabled);
  const hasNoCoupons = !getCoupons.isLoading && getCoupons.data?.length === 0;
  const showCouponsContent =
    !getCouponsConfiguration.isLoading && isCouponsEnabled && !hasNoCoupons;

  const { isActionEnabled } = useUserPermissions();
  const canManageCoupons = isActionEnabled(
    null,
    EPermissionGroup.OFFERS,
    EPermissionAction.MANAGE,
    publisherId
  );

  const isDisabledDueToPermissions = !canUserEdit || !canManageCoupons;

  const {
    couponNameValue,
    handleCouponNameChange,
    promoCodeValue,
    handlePromoCodeChange,
    discountValue,
    handleDiscountChange,
    statusValues,
    handleStatusChange,
    rangeDates,
    setRangeDates,
    clearFiltersButton,
    handleClearFilters
  } = useCouponsFilters();

  const handleDateChange = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) {
      setRangeDates(undefined);
      return;
    }
    setRangeDates([dayjs(startDate), dayjs(endDate)]);
  };

  const handleAddCoupon = () => {
    navigate('./form');
  };

  const handleToggleCoupons = () => {
    setIsCouponsDialogOpen(true);
  };

  return (
    <div className="w-full flex flex-col gap-4 p-6 pt-4">
      <Header
        onAddCoupon={handleAddCoupon}
        isCouponsEnabled={isCouponsEnabled}
        canUserEdit={canUserEdit}
        canManageCoupons={canManageCoupons}
      />
      {!getCouponsConfiguration.isLoading && (
        <CouponsStatusToggle
          isActive={isCouponsEnabled}
          onToggle={handleToggleCoupons}
          disabled={isDisabledDueToPermissions}
        />
      )}
      {!getCouponsConfiguration.isLoading &&
        (!isCouponsEnabled || hasNoCoupons) && (
          <div className="flex items-center justify-center w-full min-h-[calc(100vh-250px)]">
            <EmptyState
              title={
                !isCouponsEnabled
                  ? 'Start using coupons'
                  : 'Add your first coupon'
              }
              description={
                !isCouponsEnabled ? (
                  <>
                    To create coupons and allow customers to add discount codes,
                    you must
                    <br />
                    first activate the Coupons feature.
                  </>
                ) : (
                  <>
                    Create promotional discount codes with custom settings,
                    track redemption
                    <br />
                    and measure campaign performance.
                  </>
                )
              }
              icon="fa-regular fa-tag"
              buttonText={
                !isCouponsEnabled ? 'Activate Coupons' : 'Create Coupon'
              }
              onClick={
                !isCouponsEnabled ? handleToggleCoupons : handleAddCoupon
              }
              buttonLeftIcon={
                !isCouponsEnabled ? (
                  <FontAwesomeIcon icon="fa-regular fa-lock-open" />
                ) : undefined
              }
            />
          </div>
        )}
      {showCouponsContent && (
          <>
            <CouponsFilterBar
              couponNameValue={couponNameValue}
              onCouponNameChange={handleCouponNameChange}
              promoCodeValue={promoCodeValue}
              onPromoCodeChange={handlePromoCodeChange}
              discountValue={discountValue}
              onDiscountChange={handleDiscountChange}
              statusValues={statusValues}
              onStatusChange={handleStatusChange}
              rangeDates={rangeDates}
              onDateChange={handleDateChange}
              clearFiltersButton={clearFiltersButton}
              handleClearFilters={handleClearFilters}
              couponsData={getCoupons?.data}
            />
            <div className="flex-1 overflow-auto">
              <Outlet
                context={{
                  couponNameValue,
                  promoCodeValue,
                  discountValue,
                  statusValues,
                  rangeDates,
                  isCouponsEnabled,
                  isCouponsConfigLoading: getCouponsConfiguration.isLoading,
                  canUserEdit,
                  canManageCoupons,
                  isDisabledDueToPermissions
                }}
              />
            </div>
          </>
        )}
      <CouponsToggleDialog
        isOpen={isCouponsDialogOpen}
        onClose={() => setIsCouponsDialogOpen(false)}
        isCouponsEnabled={isCouponsEnabled}
        isLoading={updateCouponsConfiguration.isLoading}
        onConfirm={() =>
          updateCouponsConfiguration.mutate(
            { enabled: !isCouponsEnabled },
            {
              onError: () => setIsCouponsDialogOpen(false),
              onSuccess: () => setIsCouponsDialogOpen(false)
            }
          )
        }
      />
    </div>
  );
};

export default CouponsLayout;
